// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import { Loader2, RefreshCw, Users2, MapPin, ShieldAlert } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import PatientLanguageBadge from '../../components/ui/PatientLanguageBadge'
import ConfirmDialog from '../../components/ui/ConfirmDialog'

type StaffingBody = Parameters<typeof api.updateCareRxStaffing>[1]
/** A staff attempt the server refused with 422 blockers_open — held until the manager gives an override reason. */
interface BlockedAssign { visitId: string; nurseId: string; territoryOverride: boolean; blockers: string[] }

/**
 * D-CRX-56 R-2 (owner ruling): Staffing moves OFF the POT tab into its own tab. The staffing rule:
 * when staffing a nurse to a ready patient, read the patient's ZIP and offer the IN-AREA nurses
 * (roster territory_zips vs the enrollment ZIP — ranked server-side with prior-nurse continuity,
 * leave + expired credentials excluded, capacity warnings); when nobody in-area is available, the
 * coordinator can OVERRIDE and assign any roster nurse, even outside the area. View interim per the
 * stated rules — the owner's SharePoint list export will refine the columns.
 */
export default function CareRxStaffing() {
  const [board, setBoard] = useState<any[]>([])
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(true)
  const [openFor, setOpenFor] = useState<string | null>(null)
  const [sugs, setSugs] = useState<any[]>([])
  const [allNurses, setAllNurses] = useState<api.NurseRow[]>([])
  const [override, setOverride] = useState(false)
  const [busy, setBusy] = useState(false)
  const [cantFor, setCantFor] = useState<string | null>(null)
  const [cantCat, setCantCat] = useState('no_nurse_in_territory')
  // Owner review 2026-08-13: missed-reason inline (same shape as can't-staff) — window.prompt
  // is off the shared kit and eats the reason on Escape with no way back.
  const [missedFor, setMissedFor] = useState<string | null>(null)
  const [missedReason, setMissedReason] = useState('')
  // B-2 blocker override: the server supports staffing past open blockers with an audited reason;
  // the UI never offered it, so a nurse manager was stuck at the red "cannot staff while blockers are open".
  const [blocked, setBlocked] = useState<BlockedAssign | null>(null)
  const [blockedErr, setBlockedErr] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    api.getCareRxStaffingBoard().then(d => { setBoard(d.items ?? []); setMsg('') })
      .catch((e: any) => setMsg(e?.body?.error === 'pending_migration' ? 'The staffing board awaits migration 280.' : 'Failed to load the board'))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const openStaff = async (visitId: string) => {
    setOpenFor(visitId); setOverride(false); setSugs([]); setMsg('')
    try {
      const s = await api.getCareRxStaffingSuggestions(visitId)
      setSugs(s.items ?? [])
      if (!allNurses.length) setAllNurses((await api.listCareRxNurses()).items.filter(n => n.active))
    } catch (e: any) { setMsg(e?.body?.message ?? 'Could not load suggestions') }
  }
  const assign = async (visitId: string, nurseId: string, isOverride: boolean) => {
    setBusy(true); setMsg(''); setNote('')
    try {
      // Owner review 2026-08-13: out-of-area assigns are RECORDED (the UI promised it; now true).
      await api.updateCareRxStaffing(visitId, { action: 'staff', nurse_id: nurseId, ...(isOverride ? { territory_override: true } : {}) })
      setOpenFor(null); load()
    } catch (e: unknown) {
      const err = e instanceof api.ApiError ? e : null
      if (err?.body?.error === 'blockers_open') {
        const blockers: string[] = Array.isArray(err.body.blockers) ? err.body.blockers.map(String) : []
        setBlockedErr(null); setBlocked({ visitId, nurseId, territoryOverride: isOverride, blockers })
      } else setMsg(err?.body?.message ?? 'Refused.')
    } finally { setBusy(false) }
  }
  // The audited override: same assign, plus override=true + override_reason (the server names the fields
  // in its 422 hint); the territory flag from the original attempt is kept so that audit row still lands.
  const assignOverride = async (reason: string) => {
    if (!blocked) return
    setBusy(true); setBlockedErr(null)
    try {
      await api.updateCareRxStaffing(blocked.visitId, {
        action: 'staff', nurse_id: blocked.nurseId, override: true, override_reason: reason,
        ...(blocked.territoryOverride ? { territory_override: true } : {}),
      })
      setBlocked(null); setOpenFor(null)
      setNote(`Staffed with blockers open (${blocked.blockers.join('; ')}) — the override and your reason are recorded in the audit log.`)
      load()
    } catch (e: unknown) {
      setBlockedErr(e instanceof api.ApiError ? (e.body?.message ?? e.body?.error ?? 'Refused.') : 'Refused.')
    } finally { setBusy(false) }
  }
  const act = async (visitId: string, action: string, extra?: Omit<StaffingBody, 'action'>) => {
    setBusy(true); setMsg(''); setNote('')
    try { await api.updateCareRxStaffing(visitId, { action, ...extra }); setCantFor(null); load() }
    catch (e: any) { setMsg(e?.body?.message ?? 'Refused.') } finally { setBusy(false) }
  }

  const inArea = sugs.filter(s => s.territory_fit)
  const nearby = sugs.filter(s => !s.territory_fit)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><Users2 size={16} /> Staffing</div>
        <span style={{ ...mut, fontSize: 12 }}>who must be seen first, and which nurse covers that area</span>
        <button onClick={load} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer' }}><RefreshCw size={14} /></button>
      </div>
      {msg && <div style={{ fontSize: 12.5, color: '#b45309' }}>{msg}</div>}
      {note && <div role="status" style={{ fontSize: 12.5, color: '#15803d' }}>{note}</div>}
      <ConfirmDialog open={blocked !== null} title="Assign anyway (override)" confirmLabel="Assign anyway (override)" tone="danger"
        busy={busy} error={blockedErr}
        body={blocked && (
          <div style={{ fontSize: 13 }}>
            <div>The visit cannot normally be staffed while these blockers are open:</div>
            <ul style={{ margin: '6px 0 8px', paddingLeft: 18 }}>{blocked.blockers.map(b => <li key={b}>{b}</li>)}</ul>
            <div>You may staff it anyway. The override, your reason and the nurse are recorded in the audit log.</div>
          </div>
        )}
        requireReason={{ label: 'Override reason (audited)', minLength: 5, placeholder: 'Why the visit must be staffed now' }}
        onCancel={() => { setBlocked(null); setBlockedErr(null) }}
        onConfirm={({ reason }) => { void assignOverride(reason) }} />
      {loading ? <div style={{ ...mut, fontSize: 12.5 }}><Loader2 size={13} className="spin" /> Loading…</div>
        : board.length === 0 ? <div style={{ ...mut, fontSize: 12.5 }}>Nothing needs staffing.</div> : (
          [...board].sort((a, b0) => b0.urgency - a.urgency || b0.days_late - a.days_late).map(v => (
            <div key={v.visit_id} style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
                <b style={{ color: v.urgency >= 4 ? '#b91c1c' : v.urgency === 3 ? '#b45309' : 'var(--color-text)' }}>U{v.urgency}</b>
                <span>{v.patient_name}<PatientLanguageBadge patientId={v.patient_id} /> · {v.drug_name || 'therapy'}</span>
                {v.first_dose && <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>FIRST DOSE</span>}
                <span style={mut}>{v.days_late > 0 ? `${v.days_late}d past due` : 'on time'}</span>
                {/* Owner UI surf 2026-08-13: the board says WHO covers a staffed visit, not just the status word. */}
                <span style={{ fontWeight: 700, color: v.staffing_status === 'confirmed' ? '#15803d' : v.staffing_status === 'cant_staff' ? '#b91c1c' : '#0e7490' }}>
                  {String(v.staffing_status).replace('_', ' ')}{v.nurse_name ? ` — ${v.nurse_name}` : ''}</span>
                {v.missed_count > 0 && <span style={{ color: '#b91c1c' }}>misses: {v.missed_count}</span>}
              </div>
              {v.blockers?.length > 0 && <div style={{ color: '#b45309', fontSize: 12, marginTop: 2 }}>Blocked: {v.blockers.join(' · ')}</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {['unassigned', 'pending', 'missed', 'cant_staff'].includes(v.staffing_status) && (
                  <button style={sbtn} onClick={() => openStaff(v.visit_id)}>Staff…</button>
                )}
                {v.staffing_status === 'staffed' && <button style={sbtn} disabled={busy} onClick={() => act(v.visit_id, 'confirm')}>Confirm with patient</button>}
                <button style={sbtn} onClick={() => setCantFor(cantFor === v.visit_id ? null : v.visit_id)}>Can't staff</button>
                <button style={sbtn} disabled={busy} onClick={() => { setMissedFor(missedFor === v.visit_id ? null : v.visit_id); setMissedReason('') }}>Missed</button>
              </div>

              {missedFor === v.visit_id && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input value={missedReason} onChange={e => setMissedReason(e.target.value)} placeholder="Missed-visit reason (required)"
                    style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: 7, minWidth: 240 }} />
                  <button style={{ ...sbtn, background: '#b45309', color: '#fff', borderColor: '#b45309' }} disabled={busy || !missedReason.trim()}
                    onClick={() => { void act(v.visit_id, 'missed', { reason: missedReason.trim() }); setMissedFor(null) }}>Record missed</button>
                </div>
              )}

              {cantFor === v.visit_id && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <SearchableSelect value={cantCat} onChange={setCantCat} style={{ fontSize: 12, minWidth: 200 }} ariaLabel="Can't-staff category"
                    options={['no_nurse_in_territory', 'none_available_that_week', 'patient_unreachable', 'patient_refused_dates', 'home_access_safety', 'credential_gap'].map(c => ({ value: c, label: c }))} />
                  <button style={{ ...sbtn, background: '#b91c1c', color: '#fff', borderColor: '#b91c1c' }} disabled={busy} onClick={() => act(v.visit_id, 'cant_staff', { category: cantCat })}>Record can't-staff</button>
                </div>
              )}

              {openFor === v.visit_id && (
                <div style={{ marginTop: 10, borderTop: '1px dashed var(--color-border)', paddingTop: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: .3, color: 'var(--color-text-soft)', display: 'flex', gap: 6, alignItems: 'center' }}>
                    <MapPin size={13} /> IN-AREA NURSES <span style={{ fontWeight: 400, textTransform: 'none' }}>— territory matches the patient's ZIP</span>
                  </div>
                  {inArea.length === 0 && <div style={{ ...mut, fontSize: 12.5, margin: '4px 0' }}>No in-area nurse available (leave and expired credentials are excluded).</div>}
                  {inArea.map((s: any) => nurseRow(s, () => assign(v.visit_id, s.nurse_id, false), busy))}
                  {nearby.length > 0 && <>
                    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: .3, color: 'var(--color-text-soft)', marginTop: 8 }}>OTHER ELIGIBLE (out of area)</div>
                    {nearby.map((s: any) => nurseRow(s, () => assign(v.visit_id, s.nurse_id, true), busy))}
                  </>}
                  <div style={{ marginTop: 8 }}>
                    <button style={{ ...sbtn, display: 'inline-flex', gap: 5, alignItems: 'center' }} onClick={() => setOverride(o => !o)}>
                      <ShieldAlert size={12} /> {override ? 'Hide override list' : 'Override — assign ANY nurse'}
                    </button>
                    {override && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ ...mut, fontSize: 11.5, marginBottom: 4 }}>Owner rule: when nobody in-area is available you may assign any roster nurse, even outside the area. The assignment is recorded as an override.</div>
                        {allNurses.map(n => (
                          <div key={n.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, padding: '4px 0', borderBottom: '1px dashed var(--color-border)' }}>
                            <b>{n.display_name}</b>
                            <span style={mut}>{n.territory_zips || 'no territory'}</span>
                            {n.competencies && <span style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>{n.competencies}</span>}
                            <button style={{ ...sbtn, marginLeft: 'auto' }} disabled={busy} onClick={() => assign(v.visit_id, n.nurse_id, true)}>Assign (override)</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button style={{ ...sbtn, marginTop: 8 }} onClick={() => setOpenFor(null)}>Close</button>
                </div>
              )}
            </div>
          ))
        )}
    </div>
  )
}

function nurseRow(s: any, onAssign: () => void, busy: boolean) {
  return (
    <div key={s.nurse_id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, padding: '4px 0', borderBottom: '1px dashed var(--color-border)' }}>
      <b>{s.display_name}</b>
      {s.prior_nurse && <span style={{ fontSize: 10, fontWeight: 800, borderRadius: 8, padding: '1px 7px', background: '#ede9fe', color: '#6d28d9' }}>PRIOR NURSE</span>}
      {s.territory_fit && <span style={{ fontSize: 10, fontWeight: 800, borderRadius: 8, padding: '1px 7px', background: '#e9f7ec', color: '#0a7a1d' }}>in area</span>}
      {s.capacity_warning && <span style={{ fontSize: 11, color: '#b45309' }}>⚠ {s.day_load} visits that day</span>}
      <button style={{ ...sbtn, marginLeft: 'auto' }} disabled={busy} onClick={onAssign}>Assign</button>
    </div>
  )
}

const mut: CSSProperties = { color: 'var(--color-text-soft, #6b7280)' }
const sbtn: CSSProperties = { fontSize: 12, border: '1px solid var(--color-border)', borderRadius: 7, padding: '3px 10px', background: 'transparent', cursor: 'pointer', color: 'var(--color-text)' }
